/**
 * Google Identity Services, for the staff "Sign in with Google" button (GO-568).
 *
 * **Loaded only when the sign-in sheet shows the button**, the way the broadcast SDK loads
 * only on a show screen: a guest looking at an auction should not download Google's script.
 *
 * Google's button hands back an **ID token** (`credential`), which is all we want from it:
 * no OAuth redirect, no access to anything at Google, and nothing for us to keep. The token
 * goes straight to the pool's custom challenge, which checks it (`apps/auth`).
 */

const SCRIPT = 'https://accounts.google.com/gsi/client';

interface GoogleIdentity {
  initialize(config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_button?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, string | number>): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdentity } };
  }
}

let loading: Promise<GoogleIdentity> | undefined;
let initializedFor: string | undefined;
/** The sheet open now. Google takes one callback per page, so it is routed from here. */
let receive: ((credential: string) => void) | undefined;

function load(): Promise<GoogleIdentity> {
  loading ??= new Promise<GoogleIdentity>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => {
      const id = window.google?.accounts?.id;
      if (id) resolve(id);
      else reject(new Error('Google sign-in loaded without its API'));
    };
    script.onerror = () => {
      // Not cached: a blocked or dropped load is tried again the next time the sheet opens.
      loading = undefined;
      script.remove();
      reject(new Error("Couldn't load Google sign-in."));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Draw Google's button into `parent`; `onCredential` gets the ID token when someone signs in.
 * Returns a function that stops this caller from receiving it.
 */
export async function renderGoogleButton(
  parent: HTMLElement,
  clientId: string,
  onCredential: (credential: string) => void,
): Promise<() => void> {
  const id = await load();
  if (initializedFor !== clientId) {
    id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response.credential) receive?.(response.credential);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      use_fedcm_for_button: true,
    });
    initializedFor = clientId;
  }
  receive = onCredential;
  // Google's own sizes and words, so the button is the one people recognize.
  id.renderButton(parent, {
    type: 'standard',
    theme: 'filled_black',
    size: 'large',
    text: 'signin_with',
    shape: 'pill',
  });
  return () => {
    if (receive === onCredential) receive = undefined;
  };
}
